/**
 * TanStack Query keys per task.md 3.4
 */
export type AmbassadorEventStatusFilter =
  | "all"
  | "draft"
  | "pending_approval"
  | "approved"
  | "completed"
  | "rejected"
  | "cancelled";

export const queryKeys = {
  payment: {
    methods: ["payment", "methods"] as const,
    catalogDetail: (slug: string, authed: boolean) =>
      ["payment", "catalogDetail", slug, authed] as const,
    orderDetail: (orderId: string) => ["payment", "orderDetail", orderId] as const,
  },
  auth: {
    me: ["auth", "me"] as const,
  },
  catalog: {
    list: (opts: { type?: string; search?: string; tag?: string; page?: number; limit?: number; sort?: string }) =>
      ["catalog", "list", opts] as const,
    detail: (slug: string) => ["catalog", "detail", slug] as const,
  },
  courses: {
    list: (params: {
      category?: string;
      delivery_type?: string;
      delivery_types?: string;
      search?: string;
      sort?: string;
      page?: number;
      limit?: number;
    }) => ["courses", "list", params] as const,
    detail: (slug: string) => ["courses", "detail", slug] as const,
    pricingPlans: (slug: string) => ["courses", "pricingPlans", slug] as const,
    eligibilityStatus: (idOrSlug: string) =>
      ["courses", "eligibilityStatus", idOrSlug] as const,
  },
  lessons: {
    detail: (lessonId: string, previewKey = "") =>
      ["lessons", "detail", lessonId, previewKey] as const,
    // `PG-COURSE01`: the coding exercise's own attempts, keyed by lesson.
    exerciseSubmissions: (lessonId: string) =>
      ["lessons", "exercise", lessonId, "submissions"] as const,
    exerciseSubmission: (lessonId: string, submissionId: string) =>
      ["lessons", "exercise", lessonId, "submission", submissionId] as const,
  },
  me: {
    library: ["me", "library"] as const,
    orders: (opts: { page?: number; limit?: number; status?: string }) =>
      ["me", "orders", opts] as const,
    profile: ["me", "profile"] as const,
    stats: ["me", "stats"] as const,
    liveClasses: (opts: { range: "upcoming" | "past"; limit: number; courseId?: string }) =>
      ["me", "live-classes", opts.range, opts.limit, opts.courseId ?? ""] as const,
    referralStats: ["me", "referralStats"] as const,
  },
  dashboard: {
    overview: ["dashboard", "overview"] as const,
  },
  gamification: {
    profile: ["gamification", "profile"] as const,
    badgeCatalog: ["gamification", "badgeCatalog"] as const,
    myBadges: ["gamification", "myBadges"] as const,
    activityFeed: (opts: { page?: number; limit?: number }) =>
      ["gamification", "activity", opts.page ?? 1, opts.limit ?? 20] as const,
    activityPages: ["gamification", "activityPages"] as const,
    calendar: (year: number | null) => ["gamification", "calendar", year ?? "last"] as const,
    leaderboard: (period: "weekly" | "monthly" | "alltime", limit: number) =>
      ["gamification", "leaderboard", period, limit] as const,
  },
  progress: {
    course: (courseId: string, previewKey = "") =>
      ["progress", "course", courseId, previewKey] as const,
  },
  certificates: {
    mine: ["certificates", "mine"] as const,
    /** `EX-CRT01`: the account's contest certificates, their own list. */
    contestMine: ["certificates", "contest-mine"] as const,
    byCourse: (courseId: string) => ["certificates", "byCourse", courseId] as const,
    verify: (certificateId: string) => ["certificates", "verify", certificateId] as const,
  },
  activity: {
    mine: ["activity", "mine"] as const,
  },
  notifications: {
    mine: ["me", "notifications"] as const,
    preferences: ["me", "notification-preferences"] as const,
  },
  /**
   * `SUP-WEB01`: the person's support tickets, and a visitor's one ticket. The
   * visitor's key never holds the link: the page has one link at a time.
   */
  support: {
    mine: ["me", "support", "tickets"] as const,
    ticket: (number: number) => ["me", "support", "ticket", number] as const,
    guest: ["support", "guest-ticket"] as const,
    /**
     * `SUP-AI03`: the help chat. A person's reads sit under `me`; a visitor's
     * belong to the browser's cookie, never to an account.
     */
    chatStatus: (audience: "person" | "visitor") =>
      [audience === "person" ? "me" : "visitor", "support", "chat", "status"] as const,
    chats: (audience: "person" | "visitor") => [audience === "person" ? "me" : "visitor", "support", "chat", "list"] as const,
    chat: (id: string) => ["me", "support", "chat", "one", id] as const,
    /** `SUP-HRS01`: the public support hours, the same for everyone. */
    hours: ["support", "hours"] as const,
  },
  /** `EX-COL05`: the invitee's side of a team invitation (`features/invitations`). */
  invitations: {
    all: ["me", "invitations"] as const,
    list: (status: "pending" | "answered" | "all") => ["me", "invitations", "list", status] as const,
    detail: (id: string) => ["me", "invitations", "detail", id] as const,
    forTarget: (kind: string, id: string) => ["me", "invitations", "target", kind, id] as const,
  },
  /** `HR-ORG01b`: the Progsity Hire company workspace (`features/hire`). */
  hire: {
    all: ["hire"] as const,
    myCompanies: ["hire", "me", "companies"] as const,
    company: (id: string) => ["hire", "company", id] as const,
    members: (id: string) => ["hire", "company", id, "members"] as const,
    invitations: (id: string) => ["hire", "company", id, "invitations"] as const,
    /** `HR-BIL01`: the balance, a page of the ledger, and the owner's billing. */
    credits: (id: string) => ["hire", "company", id, "credits"] as const,
    creditEntries: (id: string, page: number) => ["hire", "company", id, "credits", "entries", page] as const,
    billing: (id: string) => ["hire", "company", id, "billing"] as const,
    /** `EX-TAL01`: the rounds that name the company a sponsor, and one round's pass. */
    talentRounds: (id: string) => ["hire", "company", id, "talent"] as const,
    talentPass: (id: string, contestId: string) => ["hire", "company", id, "talent", contestId] as const,
    /** `HR-JOB01c`: the Jobs list, a job, its board and a candidate's panel. */
    jobs: (id: string) => ["hire", "company", id, "jobs"] as const,
    jobList: (id: string, filter: string) => ["hire", "company", id, "jobs", "list", filter] as const,
    job: (id: string, jobId: string) => ["hire", "company", id, "jobs", "job", jobId] as const,
    board: (id: string, jobId: string) => ["hire", "company", id, "jobs", "job", jobId, "board"] as const,
    application: (id: string, applicationId: string) => ["hire", "company", id, "jobs", "application", applicationId] as const,
  },
  announcements: {
    feed: (opts: { page?: number; limit?: number }) =>
      ["me", "announcements", opts.page ?? 1, opts.limit ?? 20] as const,
  },
  assignments: {
    mine: (opts: { page?: number; limit?: number }) =>
      ["assignments", "mine", opts.page ?? 1, opts.limit ?? 20] as const,
    public: (opts: { page?: number; limit?: number }) =>
      ["assignments", "public", opts.page ?? 1, opts.limit ?? 20] as const,
    byCode: (accessCode: string) =>
      ["assignments", "byCode", accessCode] as const,
    workspace: (assignmentId: string) =>
      ["assignments", "workspace", assignmentId] as const,
    creatorList: (opts: { page?: number; limit?: number; status?: string; search?: string }) =>
      ["assignments", "creatorList", opts] as const,
    creatorDetail: (assignmentId: string) =>
      ["assignments", "creatorDetail", assignmentId] as const,
    creatorAccessGrants: (assignmentId: string) =>
      ["assignments", "creatorAccessGrants", assignmentId] as const,
    creatorSubmissions: (assignmentId: string, opts: { page?: number; limit?: number; status?: string }) =>
      ["assignments", "creatorSubmissions", assignmentId, opts] as const,
    creatorSubmissionDetail: (assignmentId: string, submissionId: string) =>
      ["assignments", "creatorSubmissionDetail", assignmentId, submissionId] as const,
  },
  /** `GR-PRT01` */
  partner: {
    page: ["partner", "page"] as const,
  },
  ambassador: {
    dashboard: ["ambassador", "dashboard"] as const,
    leaderboard: (period: "month" | "quarter" | "all") =>
      ["ambassador", "leaderboard", period] as const,
    certificateEligibility: ["ambassador", "certificateEligibility"] as const,
    application: ["ambassador", "application"] as const,
    events: (status?: AmbassadorEventStatusFilter) =>
      ["ambassador", "events", status ?? "all"] as const,
    /** `AMB-06` */
    meetings: (scope: "upcoming" | "past") => ["ambassador", "meetings", scope] as const,
  },
  quiz: {
    catalog: (params: {
      category?: string;
      difficulty?: string;
      language?: string;
      assessmentType?: string;
      search?: string;
      page?: number;
      limit?: number;
    }) => ["quiz", "catalog", params] as const,
    detail: (slug: string) => ["quiz", "detail", slug] as const,
    embed: (slug: string) => ["quiz", "embed", slug] as const,
    byCode: (code: string) => ["quiz", "byCode", code] as const,
    mySessions: (quizId: string) => ["quiz", "mySessions", quizId] as const,
    activeSession: (quizId: string) => ["quiz", "activeSession", quizId] as const,
    result: (sessionId: string) => ["quiz", "result", sessionId] as const,
    // Student history
    myHistory: (params: { page?: number; limit?: number; status?: string }) =>
      ["quiz", "myHistory", params] as const,
    // Creator
    creatorList: (params: { page?: number; limit?: number; status?: string }) =>
      ["quiz", "creatorList", params] as const,
    creatorDetail: (id: string) => ["quiz", "creatorDetail", id] as const,
    leaderboard: (slug: string, page?: number, limit?: number) =>
      ["quiz", "leaderboard", slug, page ?? 1, limit ?? 50] as const,
    creatorLeaderboard: (quizId: string, page?: number, limit?: number) =>
      ["quiz", "creatorLeaderboard", quizId, page ?? 1, limit ?? 50] as const,
    creatorSessions: (quizId: string, params: { page?: number; limit?: number }) =>
      ["quiz", "creatorSessions", quizId, params] as const,
    creatorSessionDetail: (sessionId: string) =>
      ["quiz", "creatorSessionDetail", sessionId] as const,
    analytics: (quizId: string) => ["quiz", "analytics", quizId] as const,
    advancedAnalytics: (quizId: string) => ["quiz", "advancedAnalytics", quizId] as const,
    subscriptionPlans: ["quiz", "subscriptionPlans"] as const,
    mySubscription: ["quiz", "mySubscription"] as const,
    // Template gallery
    templateGallery: (params: {
      category?: string;
      difficulty?: string;
      assessmentType?: string;
      language?: string;
      search?: string;
      sort?: string;
      page?: number;
      limit?: number;
    }) => ["quiz", "templateGallery", params] as const,
    templateDetail: (slug: string) => ["quiz", "templateDetail", slug] as const,
    // Exam coding task workspace (EX-EXM02)
    codingTask: (sessionId: string, questionId: string) =>
      ["quiz", "codingTask", sessionId, questionId] as const,
    codingSubmissions: (sessionId: string, questionId: string) =>
      ["quiz", "codingSubmissions", sessionId, questionId] as const,
    // Coding task authoring (EX-EXM01A) — the two `problem.write` reads the
    // builder needs. `gradedRuntimes` is the graded catalogue, never Playground's.
    judgeProblems: (params: { search?: string; visibility?: string; page?: number }) =>
      ["quiz", "judgeProblems", params] as const,
    judgeProblem: (id: string) => ["quiz", "judgeProblem", id] as const,
    gradedRuntimes: ["quiz", "gradedRuntimes"] as const,
    // Coding task authoring (EX-EXM01B) — the picker now has two sources, and
    // the source is part of the key on purpose: the authoring list and the exam
    // bank return different payloads for the same problem id, so sharing a cache
    // entry would let one role's view leak into another's on a shared machine.
    attachableProblems: (
      source: string,
      params: { search?: string; page?: number },
    ) => ["quiz", "attachableProblems", source, params] as const,
    attachableProblem: (source: string, id: string) =>
      ["quiz", "attachableProblem", source, id] as const,
  },
  playground: {
    languages: ["playground", "languages"] as const,
    runtimes: ["playground", "runtimes"] as const,
    health: ["playground", "health"] as const,
    idePlan: ["playground", "idePlan"] as const,
    folders: ["playground", "folders"] as const,
    mySnippets: (params: {
      page?: number;
      limit?: number;
      languageId?: string;
      folderId?: string;
      /** Server-side title search (`PG-UX06`); part of the key so a response never lands under another query. */
      q?: string;
    }) => ["playground", "mySnippets", params] as const,
    snippetById: (id: string) => ["playground", "snippet", "id", id] as const,
    /** Owner version history for one snippet (`PG-IDE05`). */
    snippetVersions: (id: string) => ["playground", "snippet", "versions", id] as const,
    snippetVersion: (id: string, versionId: string) =>
      ["playground", "snippet", "versions", id, versionId] as const,
    sharedSnippet: (slug: string) => ["playground", "snippet", "share", slug] as const,
  },
};
